import { useState, useEffect, useRef, useCallback } from "react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import AIToolbar from "@/components/studio/AIToolbar";
import AudioBar from "@/components/studio/AudioBar";
import RichEditor from "@/components/studio/RichEditor";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Bold, Italic, Underline, Heading2, Heading3, Pilcrow, Quote, List } from "lucide-react";

function escapeHtml(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function htmlifyParagraphs(text) {
  return (text || "")
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}
function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return tmp.textContent || "";
}

export default function Editor({ chapter, project, onChapterUpdated }) {
  const { t } = useI18n();
  const [title, setTitle] = useState(chapter.title);
  const [content, setContent] = useState(chapter.content);
  const [status, setStatus] = useState("saved");
  const editorRef = useRef(null);
  const lastRange = useRef(null);
  const saveTimer = useRef(null);
  const skipSave = useRef(true);

  useEffect(() => {
    skipSave.current = true;
    setTitle(chapter.title);
    setContent(chapter.content);
    setStatus("saved");
    lastRange.current = null;
  }, [chapter.id]);

  const doSave = useCallback(async (t2, c2) => {
    setStatus("saving");
    try {
      const { data } = await api.put(`/chapters/${chapter.id}`, { title: t2, content: c2 });
      onChapterUpdated(data);
      setStatus("saved");
    } catch {
      setStatus("saved");
    }
  }, [chapter.id, onChapterUpdated]);

  const scheduleSave = useCallback((t2, c2) => {
    setStatus("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(t2, c2), 900);
  }, [doSave]);

  // Title changes
  useEffect(() => {
    if (skipSave.current) { skipSave.current = false; return; }
    scheduleSave(title, content);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const onEditorChange = (html) => {
    setContent(html);
    scheduleSave(title, html);
  };

  const fmt = (cmd, val) => {
    const el = editorRef.current;
    if (!el) return;
    document.execCommand(cmd, false, val);
    const html = el.innerHTML;
    setContent(html);
    scheduleSave(title, html);
  };

  const getSelection = () => {
    const el = editorRef.current;
    const s = window.getSelection();
    let text = "";
    if (s && s.rangeCount && !s.isCollapsed && el && el.contains(s.anchorNode)) {
      text = s.toString();
      lastRange.current = s.getRangeAt(0).cloneRange();
    } else {
      lastRange.current = null;
    }
    return { text, full: el ? el.textContent : stripHtml(content), hasSelection: !!text };
  };

  const applyResult = (result, sel, action) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    if (sel.hasSelection && lastRange.current) {
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(lastRange.current);
      document.execCommand("insertText", false, result);
    } else if (action === "continue") {
      el.insertAdjacentHTML("beforeend", htmlifyParagraphs(result));
    } else {
      el.innerHTML = htmlifyParagraphs(result);
    }
    const html = el.innerHTML;
    setContent(html);
    scheduleSave(title, html);
  };

  const plain = stripHtml(content);
  const words = plain.trim() ? plain.trim().split(/\s+/).length : 0;
  const context = project?.description ? `Sinopsis: ${project.description}` : "";

  const fmtBtns = [
    { icon: Bold, cmd: "bold", label: "fmt.bold", tid: "fmt-bold" },
    { icon: Italic, cmd: "italic", label: "fmt.italic", tid: "fmt-italic" },
    { icon: Underline, cmd: "underline", label: "fmt.underline", tid: "fmt-underline" },
    { sep: true },
    { icon: Heading2, cmd: "formatBlock", val: "h2", label: "fmt.h2", tid: "fmt-h2" },
    { icon: Heading3, cmd: "formatBlock", val: "h3", label: "fmt.h3", tid: "fmt-h3" },
    { icon: Pilcrow, cmd: "formatBlock", val: "p", label: "fmt.paragraph", tid: "fmt-paragraph" },
    { sep: true },
    { icon: Quote, cmd: "formatBlock", val: "blockquote", label: "fmt.quote", tid: "fmt-quote" },
    { icon: List, cmd: "insertUnorderedList", label: "fmt.list", tid: "fmt-list" },
  ];

  return (
    <div className="flex flex-col h-screen">
      <div className="flex items-center justify-between px-6 md:px-10 py-3 border-b border-white/5 bg-ink-900/60 backdrop-blur">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {status === "saving" ? (
            <span className="flex items-center gap-1.5" data-testid="editor-auto-save-status"><Loader2 className="w-3 h-3 animate-spin text-gold" /> {t("studio.saving")}</span>
          ) : (
            <span className="flex items-center gap-1.5 text-slate-500" data-testid="editor-auto-save-status"><Check className="w-3 h-3 text-emerald-500" /> {t("studio.saved")}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 font-mono">
          {words} {t("studio.words")} · {plain.length} {t("studio.chars")}
        </div>
      </div>

      {/* Formatting toolbar */}
      <div className="px-6 md:px-10 py-2 border-b border-white/5 bg-ink-800/40 flex items-center gap-1 flex-wrap">
        {fmtBtns.map((b, i) =>
          b.sep ? (
            <span key={`sep-${i}`} className="w-px h-5 bg-white/10 mx-1" />
          ) : (
            <button
              key={b.tid}
              title={t(b.label)}
              data-testid={b.tid}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => fmt(b.cmd, b.val)}
              className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-gold hover:bg-white/5 transition-colors"
            >
              <b.icon className="w-4 h-4" />
            </button>
          )
        )}
      </div>

      {/* AI toolbar */}
      <div className="px-6 md:px-10 py-3 border-b border-white/5 bg-ink-800/30">
        <AIToolbar getSelection={getSelection} applyResult={applyResult} language={project?.language || "es"} context={context} />
      </div>

      {/* Writing surface */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 md:px-4 py-10">
          <Input
            data-testid="editor-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("studio.titlePlaceholder")}
            className="border-0 bg-transparent px-0 font-serif text-3xl md:text-4xl font-bold text-slate-100 focus-visible:ring-0 h-auto py-2 placeholder:text-slate-600"
          />
          <div className="h-px bg-gradient-to-r from-gold/40 to-transparent my-4" />
          <RichEditor
            key={chapter.id}
            ref={editorRef}
            initialHTML={chapter.content}
            onChange={onEditorChange}
            placeholder={t("studio.contentPlaceholder")}
            testId="editor-content-area"
          />
        </div>
      </div>

      {/* Audio bar */}
      <div className="px-6 md:px-10 py-3 border-t border-white/5 bg-ink-800/50 backdrop-blur">
        <AudioBar getSelection={getSelection} />
      </div>
    </div>
  );
}
